import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { Toast, ToastContainer } from "react-bootstrap";

//API Imports
import { getLocationApi, getDepartmentApi, getTypeApi, getManagedByApi, getUsedByApi, getQuickReportApi, exportQuickReportApi, downloadCustomReportApi } from "./report-api";

//SVG
import ReportImg from "../../assets/svg/reportimg.svg";
import { ReactComponent as SlotCloseIcon } from "../../assets/svg/close-1.svg";

const Report = () => {
  const [selected, setSelected] = useState("quick");

  //Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);

  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [showSnackbar, setShowSnackbar] = useState(false);

  //Date

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [reportStartDate, setReportStartDate] = useState(null);
  const [reportEndDate, setReportEndDate] = useState(null);

  const fromCreatedDate = startDate ? format(startDate, "yyyy-MM-dd") : "";
  const toCreatedDate = endDate ? format(endDate, "yyyy-MM-dd") : "";

  const [formattedFromDate, setFormattedFromDate] = useState("");
  const [formattedToDate, setFormattedToDate] = useState("");

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start) {
      setFormattedFromDate(format(start, "yyyy-MM-dd"));
    } else {
      setFormattedFromDate("");
    }

    if (end) {
      setFormattedToDate(format(end, "yyyy-MM-dd"));
    } else {
      setFormattedToDate("");
    }
  };

  const handleDatesChange = (dates) => {
    const [start, end] = dates;
    setReportStartDate(start);
    setReportEndDate(end);

    if (start) {
      setFormattedFromDate(formatDate(start));
    }
    if (end) {
      setFormattedToDate(formatDate(end));
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateClear = () => {
    setReportStartDate(null);
    setReportEndDate(null);
    setFormattedFromDate("");
    setFormattedToDate("");
  };

  //Styles for Async
  const multiselectCustomStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "#f8f8f8",
      borderRadius: "8px",
      border: "0.7px solid #E5E5E5",
      padding: "2px",
      fontWeight: "500",
      fontSize: "13px",
      color: "#8790A1",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#007bff" : state.isFocused ? "#e0e0e0" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#333",
      padding: 10,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#F6F7F8",
      color: "#212129",
      borderRadius: "5px",
      padding: "2px 5px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#212129",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#212129",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "f8f9fb",
        color: "#212129",
      },
    }),
  };

  // State declarations
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const statusOptions = [
    { value: "Allocated", label: "Allocated" },
    { value: "Not Allocated", label: "Not Allocated" },
    { value: "Missing", label: "Missing" },
    { value: "Repair", label: "Repair" },
    { value: "Scrap", label: "Scrap" },
  ];

  const [selectedManagedBy, setSelectedManagedBy] = useState([]);
  const [selectedUsedBy, setSelectedUsedBy] = useState([]);

  // Change handlers
  const handleLocationChange = (selectedOptions) => {
    setSelectedLocation(selectedOptions);
  };

  const handleDepartmentChange = (selectedOptions) => {
    setSelectedDepartment(selectedOptions);
  };

  const handleTypeChange = (selectedOptions) => {
    setSelectedType(selectedOptions);
  };

  const handleManagedByChange = (selectedOptions) => {
    setSelectedManagedBy(selectedOptions);
  };

  const handleUsedByChange = (selectedOptions) => {
    setSelectedUsedBy(selectedOptions);
  };

  const getLocationData = (inputValue, callback) => {
    setTimeout(async () => {
      const response = await getLocationApi(inputValue);
      if (response && response.status === 200 && response.data?.success === true) {
        const options = response.data.suggestionlist.map((item) => ({
          value: String(item.value),
          label: String(item.label),
        }));
        callback(options);
      } else {
        callback([]);
      }
    }, 500);
  };

  const getTypeData = (inputValue, callback) => {
    setTimeout(async () => {
      const response = await getTypeApi(inputValue);
      if (response && response.status === 200 && response.data?.success === true) {
        const options = response.data.suggestionlist.map((item) => ({
          value: String(item.value),
          label: String(item.label),
        }));
        callback(options);
      } else {
        callback([]);
      }
    }, 500);
  };

  const getDepartmentData = (inputValue, callback) => {
    setTimeout(async () => {
      const response = await getDepartmentApi(inputValue);
      if (response && response.status === 200 && response.data?.success === true) {
        const options = response.data.suggestionlist.map((item) => ({
          value: String(item.value),
          label: String(item.label),
        }));
        callback(options);
      } else {
        callback([]);
      }
    }, 500);
  };

  const getStatusData = (inputValue, callback) => {
    setTimeout(() => {
      const filtered = statusOptions.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
      callback(filtered);
    }, 200); // Optional: Simulate async delay
  };

  const getManagedByData = (inputValue, callback) => {
    setTimeout(async () => {
      const response = await getManagedByApi(inputValue);
      if (response && response.status === 200 && response.data?.success === true) {
        const options = response.data.suggestionlist.map((item) => ({
          value: String(item.value),
          label: String(item.label),
        }));
        callback(options);
      } else {
        callback([]);
      }
    }, 500);
  };

  const getUsedByData = (inputValue, callback) => {
    setTimeout(async () => {
      const response = await getUsedByApi(inputValue);
      if (response && response.status === 200 && response.data?.success === true) {
        const options = response.data.suggestionlist.map((item) => ({
          value: String(item.value),
          label: String(item.label),
        }));
        callback(options);
      } else {
        callback([]);
      }
    }, 500);
  };

  //Reset
  const handleReset = () => {
    setSelectedLocation([]);
    setSelectedDepartment([]);
    setSelectedType([]);
    setSelectedStatus([]);
    setSelectedManagedBy([]);
    setSelectedUsedBy([]);
    setStartDate(null);
    setEndDate(null);
  };

  //Get Quick Report
  const [reportOptions, setreportOptions] = useState([]);

  const handlequickChange = (option) => {
    exportQuickGet(option);
  };

  useEffect(() => {
    getReport();
  }, []);

  const getReport = async () => {
    const response = await getQuickReportApi();

    if (response && response.data?.success === true) {
      setreportOptions(response.data.suggestionlist);
    } else {
      console.warn("Unexpected response or success flag missing:", response?.data);
    }
  };

  // Quick Report Download
  const exportQuickGet = async (option) => {
    try {
      const response = await exportQuickReportApi(option.value, formattedFromDate, formattedToDate);

      setSnackbarMessage(response.data.message ?? "");
      setSnackbarStatus(response.data.success ?? false);
      handleSnackbarShow();

      if (response.status === 200 && response.data.success === true) {
        const base64String = response.data.base64;
        const fileName = response.data.file_name;
        downloadBase64File(base64String, fileName);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
    }
  };

  const downloadBase64File = (base64String, fileName) => {
    // Convert base64 string to binary array
    const binary = atob(base64String);
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    // Create blob from binary data
    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create link and trigger download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //Download Customized Report
  const downloadReport = async () => {
    try {
      const data = {
        asset_type: selectedType?.filter((item) => item.value !== "").map((item) => item.value) || [],
        assigned_to: selectedUsedBy?.filter((item) => item.value !== "").map((item) => item.value) || [],
        department_id: selectedDepartment?.filter((item) => item.value !== "").map((item) => item.value) || [],
        from_date: fromCreatedDate,
        to_date: toCreatedDate,
        location_id: selectedLocation?.filter((item) => item.value !== "").map((item) => item.value) || [],
        managed_by: selectedManagedBy?.filter((item) => item.value !== "").map((item) => item.value) || [],
        status: selectedStatus?.filter((item) => item.value !== "").map((item) => item.value) || [],
      };

      const response = await downloadCustomReportApi(selectedReport, data);

      if (response.status === 200 && response.data.success) {
        const base64String = response.data.base64;
        const fileName = "Report_" + new Date().getTime() + ".xlsx";

        const binary = atob(base64String);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          array[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([array], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleReset();
      }

      setSnackbarMessage(response.data.message);
      setSnackbarStatus(response.data.success);
      handleSnackbarShow();
    } catch (error) {
      if (error.response) {
        console.error("Error Status:", error.response.status);
        console.error("Error Data:", error.response.data);
        if (error.response.status === 400) {
          // Optional handling
        }
        setSnackbarMessage(error.response.data.message);
        setSnackbarStatus(error.response.data.success);
      } else {
        console.error("Request failed:", error.message);
        setSnackbarMessage("Unexpected error occurred");
        setSnackbarStatus(false);
      }
      handleSnackbarShow();
    } finally {
    }
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <style>{`
  .datepicker-wrapper {
    width: 100%;
  }

  .datepicker-wrapper input.custom-datepicker {
    width: 100% !important;
    height: 40px;
    background-color: #F8F8F8;
    color: #172b4d;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
  }

  .datepicker-wrapper input.custom-datepicker::placeholder {
    color: #8790A1;
    font-weight: 500;
    font-size: 13px;
  }
`}</style>

      <div style={{ fontFamily: "Lexend Deca", width: "100%", height: "100%", backgroundColor: "#F0F4F9" }}>
        <div style={{ borderRadius: "16px", backgroundColor: "#ffffff", padding: "64px", margin: "16px" }}>
          <Row>
            <Col sm={12}>
              <div style={{ borderRadius: "100px", padding: "8px", backgroundColor: "#ffffff", display: "flex", alignItems: "center", maxWidth: "fit-content", marginBottom: "16px", border: "1px solid #d4dde7" }}>
                <div onClick={() => setSelected("quick")} style={{ padding: "10px 24px", fontSize: "15px", fontWeight: selected === "quick" ? "600" : "500", borderRadius: selected === "quick" ? "100px" : "0", backgroundColor: selected === "quick" ? "#1967d2" : "transparent", color: selected === "quick" ? "#ffffff" : "#1e1e1e", cursor: "pointer" }}>
                  Quick Report
                </div>
                <div onClick={() => setSelected("custom")} style={{ padding: "10px 24px", fontSize: "15px", fontWeight: selected === "custom" ? "600" : "500", borderRadius: selected === "custom" ? "100px" : "0", backgroundColor: selected === "custom" ? "#1967d2" : "transparent", color: selected === "custom" ? "#ffffff" : "#1e1e1e", cursor: "pointer" }}>
                  Custom Report
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm={12} lg={6} style={window.innerWidth >= 992 ? { borderRight: "1px solid #d4dde7", paddingRight: 42 } : {}}>
              <div style={{ marginBottom: "28px" }}>
                {selected === "custom" && <div style={{ fontWeight: "700", fontSize: "32px", color: "#172b4d" }}>Custom report</div>}
                {selected === "quick" && <div style={{ fontWeight: "700", fontSize: "32px", color: "#172b4d" }}>Quick report</div>}
              </div>

              <div style={{ marginBottom: "64px" }}>
                {selected === "custom" && <div style={{ fontWeight: "300", fontSize: "16px", color: "#626f86" }}>Use the custom export option to download data based on your filters, making it easier to analyze and manage.</div>}
                {selected === "quick" && <div style={{ fontWeight: "300", fontSize: "16px", color: "#626f86" }}>Use the quick export option to instantly download your data with a single click for fast and easy access.</div>}
              </div>

              <div>
                <img src={ReportImg} style={{ width: "100%" }} />
              </div>
            </Col>

            <Col sm={12} lg={6} className="ps-lg-5">
              <div style={{ marginBottom: "44px" }}>
                {selected === "custom" && <div style={{ fontWeight: "600", fontSize: "20px", color: "#172b4d" }}>Get your insights your way — choose a format.</div>}
                {selected === "quick" && <div style={{ fontWeight: "600", fontSize: "20px", color: "#172b4d" }}>Fast track your report—just one click away.</div>}
              </div>

              <div>
                {selected === "custom" && (
                  <Row style={{ display: "flex", alignItems: "center" }}>
                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>
                          Report Type
                          <span style={{ fontWeight: "300", fontSize: "14px", color: "red" }}>*</span>
                        </div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <select value={selectedReport} onChange={(e) => setSelectedReport(e.target.value)} style={{ height: "40px", borderRadius: "10px", border: "1.5px solid #ebebeb", padding: "0 12px", backgroundColor: "#f8f8f8", fontWeight: "500", fontSize: "13px", color: "#8790a1", width: "100%" }}>
                            <option value="">Select Report Type</option>
                            <option value="assetreport">Asset User History</option>
                            <option value="transactionreport">Gate Log History</option>
                            <option value="activityreport">Activity</option>
                            <option value="dateentriesreport">Data Entries</option>
                            <option value="assignreport">Assignee</option>
                          </select>
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Location</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions value={selectedLocation} loadOptions={getLocationData} onChange={handleLocationChange} defaultOptions placeholder="Select Location" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Department</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions value={selectedDepartment} loadOptions={getDepartmentData} onChange={handleDepartmentChange} defaultOptions placeholder="Select Department" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Type</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions value={selectedType} loadOptions={getTypeData} onChange={handleTypeChange} defaultOptions placeholder="Select Type" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Status</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions defaultOptions={statusOptions} loadOptions={getStatusData} value={selectedStatus} onChange={setSelectedStatus} placeholder="Select Status" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Created Date</div>
                        <div style={{ fontWeight: "300", fontSize: "14px", width: "100%" }}>
                          <DatePicker selected={startDate} onChange={handleDateChange} startDate={startDate} endDate={endDate} selectsRange placeholderText="Select date range" className="custom-datepicker" wrapperClassName="datepicker-wrapper" />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "40px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Managed By</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions value={selectedManagedBy} loadOptions={getManagedByData} onChange={handleManagedByChange} defaultOptions placeholder="Select Managed By" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} lg={6} style={{ marginBottom: "40px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Used By</div>
                        <div style={{ fontWeight: "300", fontSize: "14px" }}>
                          <AsyncSelect isMulti cacheOptions value={selectedUsedBy} loadOptions={getUsedByData} onChange={handleUsedByChange} defaultOptions placeholder="Select Used By" styles={multiselectCustomStyles} isClearable={false} />
                        </div>
                      </div>
                    </Col>

                    <Col sm={12} style={{ fontWeight: "300", fontSize: "14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <button style={{ borderRadius: "100px", height: "40px", border: "1px solid #1967d2", padding: "10px 20px", backgroundColor: "#ffffff", fontWeight: "600", fontSize: "15px", color: "#1967d2", lineHeight: "100%" }} onClick={handleReset}>
                          Reset
                        </button>
                        <button
                          disabled={!selectedReport}
                          style={{
                            borderRadius: "100px",
                            height: "40px",
                            padding: "10px 20px",
                            backgroundColor: selectedReport ? "#1967d2" : "#cccccc",
                            fontWeight: "700",
                            fontSize: "14px",
                            color: "#ffffff",
                            lineHeight: "100%",
                            border: "none",
                            cursor: selectedReport ? "pointer" : "not-allowed",
                            opacity: selectedReport ? 1 : 0.6,
                          }}
                          onClick={() => {
                            if (selectedReport) {
                              downloadReport();
                            }
                          }}
                        >
                          Export
                        </button>
                      </div>
                    </Col>
                  </Row>
                )}
                {selected === "quick" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    <Row style={{ display: "flex", justifyContent: "end" }}>
                      <Col sm="auto">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ fontWeight: "500", fontSize: "14px", color: "#172b4d" }} className="d-none d-xl-block">
                              Date range :
                            </div>
                            <div>
                              <DatePicker selected={reportStartDate} onChange={handleDatesChange} startDate={reportStartDate} endDate={reportEndDate} selectsRange placeholderText="Select date range" className="form-control rounded-3 custom-datepicker" style={{ backgroundColor: "#F8F8F8", color: "#8790A1", cursor: "pointer" }} />
                            </div>
                            {reportStartDate && reportEndDate && (
                              <div style={{ cursor: "pointer" }} onClick={handleDateClear}>
                                <SlotCloseIcon />
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="container">
                      <div className="row g-3">
                        {reportOptions.map((item, index) => (
                          <div key={index} className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                            <div
                              title={item.label}
                              onClick={() => {
                                handlequickChange(item);
                                handleDateClear();
                              }}
                              style={{ cursor: "pointer", display: "flex", gap: "12px", alignItems: "center", borderRadius: "16px", padding: "12px", border: "0.7px solid #E5E5E5", backgroundColor: "#F5F5F5", boxSizing: "border-box" }}
                            >
                              <div style={{ fontWeight: "500", fontSize: "14px", color: "#172B4D", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{item.label}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <ToastContainer position="top-end">
              <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
                <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
              </Toast>
            </ToastContainer>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Report;
